import $http from '@vue-util/http';
import { errorHandler } from '@/utils/ErrorHandler';
import { TRACK_PACKAGE_ENDPOINT, TRACKING_DETAILS_ENDPOINT } from '#shared/endpoints';
import { TRACK_PACKAGE } from 'config';

import { useEnvPropsStore } from '@/stores/useEnvPropsStore';

const {
  trackPackageFallbackError,
  trackingDetails: { trackingDetailsFallbackError },
} = TRACK_PACKAGE;

export const useTrackPackageStore = defineStore('trackPackageStore', {
  state: () => ({
    trackPackage: {},
    trackingDetails: {},
    trackPackageError: '',
    trackingDetailsError: '',
    isLoading: false,
    loadingDetails: true,
    killSwitch: {},
    updateTrackPackageApiError: null,
    updateTrackDetailsApiError: null,
  }),
  actions: {
    SET_TRACK_PACKAGE_DETAILS(trackPackage) {
      this.trackPackage = trackPackage;
      this.trackPackageError = '';
    },
    SET_TRACKING_DETAILS(trackingDetails) {
      this.trackingDetails = trackingDetails;
      this.trackingDetailsError = '';
    },
    SET_TRACKING_DETAILS_ERRORS(error) {
      this.trackingDetails = null;
      this.trackingDetailsError = error;
    },
    SET_TRACK_PACKAGE_DETAILS_ERROR(error) {
      if (!error?.keepState) this.trackPackage = null;
      this.trackPackageError = error?.msg || error;
    },
    SET_TRACK_PACKAGE_DETAILS_LOADER(isLoading) {
      this.isLoading = isLoading;
    },
    SET_KILLSWITCH(killSwitch) {
      this.killSwitch = killSwitch;
    },
    SET_TRACK_PACKAGE_API_ERROR(payload) {
      this.updateTrackPackageApiError = payload;
    },
    SET_TRACK_DETAILS_API_ERROR(payload) {
      this.updateTrackDetailsApiError = payload;
    },
    async GET_TRACKING_PACKAGE_DETAILS({ orderToken, shipmentToken, trackingNos }) {
      const envPropsStore = useEnvPropsStore();
      this.SET_TRACK_PACKAGE_DETAILS_LOADER(true);
      this.SET_TRACK_PACKAGE_DETAILS_ERROR('');
      this.SET_TRACK_PACKAGE_API_ERROR(null);
      try {
        const response = await $http.get(TRACK_PACKAGE_ENDPOINT, {
          headers: {
            'X-Macys-ClientId': 'SITE',
            'X-Macys-DeviceId': envPropsStore.deviceType,
          },
          params: {
            ordertoken: orderToken,
            shipmenttoken: shipmentToken,
            ...(trackingNos && { trackingno: trackingNos }),
          },
        });
        if (!response.data?.trackPackage) {
          throw new Error();
        }
        this.SET_KILLSWITCH(response.data?.killSwitch);
        this.SET_TRACK_PACKAGE_DETAILS(response.data?.trackPackage);
      } catch (error) {
        this.SET_TRACK_PACKAGE_DETAILS_ERROR(errorHandler(error, trackPackageFallbackError));
        this.SET_TRACK_PACKAGE_API_ERROR(error?.response?.data?.errors?.error?.[0]);
      } finally {
        this.SET_TRACK_PACKAGE_DETAILS_LOADER(false);
      }
    },
    async GET_TRACKING_DETAILS(reqBody) {
      const envPropsStore = useEnvPropsStore();
      this.SET_TRACKING_DETAILS_ERRORS('');
      this.SET_TRACK_DETAILS_API_ERROR(null);
      this.loadingDetails = true;
      try {
        const response = await $http.post(`${TRACKING_DETAILS_ENDPOINT}`, reqBody, {
          headers: {
            'X-Macys-ClientId': 'SITE',
            'X-Macys-DeviceId': envPropsStore.deviceType,
          },
        });
        if (!response.data?.trackingDetail) {
          throw new Error();
        }
        this.SET_TRACKING_DETAILS(response.data.trackingDetail);
      } catch (error) {
        this.SET_TRACKING_DETAILS_ERRORS(errorHandler(error, trackingDetailsFallbackError));
        this.SET_TRACK_DETAILS_API_ERROR(error?.response?.data?.errors?.error?.[0]);
      } finally {
        this.loadingDetails = false;
      }
    },
    CLEAR_TRACK_PACKAGE() {
      this.SET_TRACK_PACKAGE_DETAILS({});
      this.SET_TRACKING_DETAILS({});
      this.SET_KILLSWITCH({});
    },
  },
});
